import { MoreHorizontal, Pencil, Power, Star, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import type { Market } from '@nucleus-commerce/api-client/admin';
import { money } from '@nucleus-commerce/core';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Badge } from '@nucleus-commerce/ui/components/badge';
import { Button } from '@nucleus-commerce/ui/components/button';
import { Card, CardContent, CardHeader, CardTitle } from '@nucleus-commerce/ui/components/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@nucleus-commerce/ui/components/dropdown-menu';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import { countryName } from '../schemas/countries';

export type MarketAction = 'edit' | 'makeDefault' | 'activate' | 'deactivate' | 'delete';

export interface MarketCardProps {
  market: Market;
  /** Shows the actions menu; false for read-only roles and closed stores. */
  editable: boolean;
  /** The store's own currency: a market in another one prices through a platform exchange rate. */
  storeCurrency: string;
  onAction: (action: MarketAction, market: Market) => void;
}

/** One market: default and status badges, country chips, currency and a rounding summary. */
export function MarketCard({ market, editable, storeCurrency, onAction }: MarketCardProps) {
  const { t } = useTranslation('markets');
  const format = useFormat();
  const rate = market.exchangeRate;
  const converts = market.currency !== storeCurrency;
  const rounding =
    market.rounding.mode === 'ending'
      ? t('rounding.summary.ending', { ending: String(market.rounding.ending).padStart(2, '0') })
      : t(`rounding.summary.${market.rounding.mode}`, {
          to: format.money(money(market.rounding.to, market.currency)),
        });

  return (
    <Card className={cn('gap-3', market.status === 'inactive' && 'opacity-75')}>
      <CardHeader className="flex flex-row items-start justify-between gap-2">
        <div className="min-w-0">
          <CardTitle className="flex flex-wrap items-center gap-2">
            <span className="truncate">{market.name}</span>
            {market.isDefault ? <Badge>{t('default')}</Badge> : null}
            <Badge
              variant="outline"
              className={cn(
                market.status === 'active'
                  ? 'bg-success/15 text-success dark:bg-success/20 border-transparent'
                  : 'text-muted-foreground',
              )}
            >
              {t(`status.${market.status}`)}
            </Badge>
          </CardTitle>
          <p className="text-muted-foreground mt-1 text-xs">
            <span className="font-mono">{market.currency}</span>
            {' · '}
            {rounding}
          </p>
          {converts ? (
            <p className="text-muted-foreground mt-1 text-xs" data-testid="market-rate">
              {rate
                ? t('rate.known', {
                    from: storeCurrency,
                    to: market.currency,
                    rate: format.number(rate.rate, { maximumFractionDigits: 4 }),
                  })
                : t('rate.unknown', { from: storeCurrency, to: market.currency })}
            </p>
          ) : null}
        </div>
        {editable ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label={t('actions.menu', { name: market.name })}
              >
                <MoreHorizontal aria-hidden />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem onSelect={() => onAction('edit', market)}>
                <Pencil aria-hidden />
                {t('actions.edit')}
              </DropdownMenuItem>
              {!market.isDefault ? (
                <DropdownMenuItem
                  disabled={market.status !== 'active'}
                  onSelect={() => onAction('makeDefault', market)}
                >
                  <Star aria-hidden />
                  {t('actions.makeDefault')}
                </DropdownMenuItem>
              ) : null}
              {!market.isDefault ? (
                <DropdownMenuItem
                  onSelect={() =>
                    onAction(market.status === 'active' ? 'deactivate' : 'activate', market)
                  }
                >
                  <Power aria-hidden />
                  {market.status === 'active' ? t('actions.deactivate') : t('actions.activate')}
                </DropdownMenuItem>
              ) : null}
              {!market.isDefault ? (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    variant="destructive"
                    onSelect={() => onAction('delete', market)}
                  >
                    <Trash2 aria-hidden />
                    {t('actions.delete')}
                  </DropdownMenuItem>
                </>
              ) : null}
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null}
      </CardHeader>
      <CardContent>
        {market.countries.length === 0 ? (
          <p className="text-muted-foreground text-xs">{t('everywhereElse')}</p>
        ) : (
          <ul className="flex flex-wrap gap-1.5" aria-label={t('fields.countries')}>
            {market.countries.map((code) => (
              <li key={code}>
                <Badge variant="secondary" title={code}>
                  {countryName(code)}
                </Badge>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
